import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Snackbar,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { frontendRoutes } from "../../utility/frontendRoutes";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getUserCart,
  updateCartRequest,
} from "../../utility/httpActions/cartActions";
import useAuthentication from "../../hooks/useAuthentication";
import CartItemCard from "../../common-components/Product/CartItemCard";
import ConvertedPrice from "../../common-components/Product/ConvertedPrice";
import { Link } from "react-router-dom";
import { trackEvent } from "../../utility/analytics";
import {
  formatCurrencyTotals,
  getEffectiveUnitPrice,
  getListedUnitPrice,
  sumByCurrency,
} from "../../utility/utilityFunctions";
import { APP_SCHEME } from "../../utility/constants";

const CheckoutModal = ({ open, onClose, isLoggedIn }) => {
  const handleOpenApp = () => {
    trackEvent("app_deep_linked", {
      source: "checkout_modal",
      destination: "cart",
    });
    window.location.href = `${APP_SCHEME}://cart`;
  };

  const cartSavedNote = isLoggedIn
    ? "Your cart is saved to your account and will be waiting for you in the app."
    : "Log in to save your cart to your account — it will sync automatically when you open the app.";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
        }}
      >
        <Typography fontWeight={700} fontSize={18}>
          Complete in the app
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Checkout, payments, and order tracking are all handled in the Ngwaahia
          app. Open it to complete your purchase.
        </Typography>

        <Button
          fullWidth
          variant="contained"
          size="large"
          onClick={handleOpenApp}
          endIcon={<OpenInNewIcon />}
          sx={{
            bgcolor: "#046287",
            "&:hover": { bgcolor: "#035070" },
            borderRadius: 2,
            mb: 2,
            textTransform: "none",
            fontWeight: 700,
          }}
        >
          Open Ngwaahia App
        </Button>

        <Box
          sx={{
            p: 2,
            bgcolor: "#f8fafc",
            borderRadius: 2,
            border: "1px solid #e5e7eb",
          }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            fontWeight={600}
            sx={{ mb: 1 }}
          >
            Don't have the app yet?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {cartSavedNote}
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const UserCart = () => {
  const { isLoggedIn } = useAuthentication();
  const queryClient = useQueryClient();
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["userCart", isLoggedIn],
    queryFn: () => getUserCart(isLoggedIn),
  });

  const { mutate: updateItem } = useMutation({
    mutationFn: (productInfo) =>
      updateCartRequest(productInfo, isLoggedIn, true),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["userCart"] }),
  });

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress sx={{ color: "#046287" }} />
      </Box>
    );
  }

  if (isError || !data) {
    return (
      <Box sx={{ textAlign: "center", mt: 8 }}>
        <Typography color="text.secondary">
          Error loading your cart. Please refresh and try again.
        </Typography>
      </Box>
    );
  }

  const cartItems = data.cartItems || [];
  const isEmpty = cartItems.length === 0;

  // At offer prices where the buyer made one (in the app): that's what
  // they're proposing. The listed subtotal is shown alongside.
  const subtotals = sumByCurrency(
    cartItems,
    (item) => item.quantity * getEffectiveUnitPrice(item),
  );
  const hasOffers = cartItems.some((item) => item.offerPriceCents != null);
  const listedSubtotals = sumByCurrency(
    cartItems,
    (item) => item.quantity * getListedUnitPrice(item),
  );
  // Estimated delivery fee: each distinct product's fee where the product
  // supports DELIVERY (the buyer picks a method at checkout, in the app).
  // Mirrors mobile's ShopProductsInCart.
  const seenDeliveryProductIds = new Set();
  const estimatedDeliveryFees = sumByCurrency(cartItems, (item) => {
    const productId = item?.product?.id || item?.product?._id;
    if (!productId || seenDeliveryProductIds.has(productId)) return 0;
    if (!item?.product?.deliveryMethods?.includes("DELIVERY")) return 0;
    seenDeliveryProductIds.add(productId);
    return Number(item.product.deliveryFee) || 0;
  });
  const hasDeliveryFee = estimatedDeliveryFees.some((t) => t.amount > 0);

  return (
    <Box sx={{ maxWidth: 760, mx: "auto", px: { xs: 2, md: 4 }, py: 4 }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>
        Your Cart
        {!isEmpty && (
          <Typography
            component="span"
            variant="body1"
            color="text.secondary"
            fontWeight={400}
            sx={{ ml: 1 }}
          >
            ({cartItems.length} {cartItems.length === 1 ? "item" : "items"})
          </Typography>
        )}
      </Typography>

      {isEmpty ? (
        <Box
          sx={{
            textAlign: "center",
            py: 10,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          <ShoppingCartOutlinedIcon sx={{ fontSize: 64, color: "#d1d5db" }} />
          <Typography variant="h6" color="text.secondary" fontWeight={600}>
            Your cart is empty
          </Typography>
          <Typography color="text.secondary" fontSize={14}>
            Browse products and add something you like.
          </Typography>
          <Button
            variant="contained"
            component={Link}
            to={frontendRoutes.HOME}
            sx={{
              mt: 1,
              bgcolor: "#046287",
              "&:hover": { bgcolor: "#035070" },
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 700,
            }}
          >
            Start shopping
          </Button>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 4,
            alignItems: { xs: "stretch", md: "flex-start" },
          }}
        >
          {/* Cart items */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {cartItems.map((item) => (
              <CartItemCard
                key={item.product?.id || item.product}
                cartItem={item}
                onUpdate={updateItem}
                onQuantityClamped={setSnackbarMessage}
              />
            ))}
          </Box>

          {/* Order summary */}
          <Box
            sx={{
              width: { md: 280 },
              flexShrink: 0,
              border: "1px solid #e5e7eb",
              borderRadius: 2,
              p: 2.5,
              boxSizing: "border-box",
              position: { md: "sticky" },
              top: { md: 80 },
            }}
          >
            <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
              Order Summary
            </Typography>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
            >
              <Typography color="text.secondary">
                Subtotal ({cartItems.length}{" "}
                {cartItems.length === 1 ? "item" : "items"})
              </Typography>
              <Typography fontWeight={600}>
                {formatCurrencyTotals(subtotals)}
              </Typography>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 2.5 }}
            >
              <Typography fontWeight={700}>
                {hasOffers ? "Total with your offers" : "Total"}
              </Typography>
              <Typography fontWeight={700} color="#046287" fontSize={18}>
                {formatCurrencyTotals(subtotals)}
              </Typography>
            </Box>
            <ConvertedPrice
              totals={subtotals}
              sx={{ mt: -2, mb: 2.5, textAlign: "right" }}
            />
            {hasOffers && (
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="right"
                sx={{ mt: -2, mb: 2.5, fontSize: 12 }}
              >
                Listed: {formatCurrencyTotals(listedSubtotals)}
              </Typography>
            )}
            {hasDeliveryFee && (
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="right"
                sx={{ mt: -2, mb: 2.5, fontSize: 12 }}
              >
                + {formatCurrencyTotals(estimatedDeliveryFees)} delivery fee (if
                delivered)
              </Typography>
            )}
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={() => {
                trackEvent("checkout_initiated", {
                  itemCount: cartItems.length,
                  // Only meaningful as one number when there's one currency.
                  ...(subtotals.length === 1 && {
                    subtotal: subtotals[0].amount,
                    currency: subtotals[0].currency,
                  }),
                });
                setCheckoutOpen(true);
              }}
              sx={{
                bgcolor: "#046287",
                "&:hover": { bgcolor: "#035070" },
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 700,
                py: 1.5,
              }}
            >
              Checkout
            </Button>
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              sx={{ mt: 1.5, fontSize: 12 }}
            >
              Checkout is completed in the Ngwaahia app
            </Typography>
          </Box>
        </Box>
      )}

      <CheckoutModal
        open={checkoutOpen}
        onClose={() => setCheckoutOpen(false)}
        isLoggedIn={isLoggedIn}
      />

      <Snackbar
        open={!!snackbarMessage}
        autoHideDuration={4000}
        onClose={() => setSnackbarMessage(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="warning"
          onClose={() => setSnackbarMessage(null)}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserCart;
